import React from "react";

/**
 * Assistant prose with its links made clickable.
 *
 * The assistant writes markdown, but the panel renders plain text, so a link
 * arrived as literal `[label](/path)` -- worst of all in Hebrew, where the
 * bracketed label and the URL are reordered on screen into something
 * unreadable. This turns just the links into anchors and leaves every other
 * character exactly as it was: the point is to make the one actionable thing
 * clickable, not to start interpreting the rest of the message as markup.
 */

// A markdown link, or a bare http(s) URL the assistant wrote without one.
const LINK_RE = /\[([^\]\n]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s<>()]+)/g;

function go(href: string) {
  // The app routes with pushState and listens for popstate, so this navigates
  // in place. A plain href would reload the whole page and take the panel --
  // and the conversation the therapist is reading -- down with it.
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

const linkStyle: React.CSSProperties = {
  color: "#1d6f6a",
  textDecoration: "underline",
  cursor: "pointer",
  fontWeight: 500,
};

export const AssistantText: React.FC<{ text: string }> = ({ text }) => {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  LINK_RE.lastIndex = 0;
  while ((match = LINK_RE.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));

    const bare = match[3];
    const href = bare ?? match[2];
    const label = bare ?? match[1];
    const internal = href.startsWith("/");

    parts.push(
      internal ? (
        <a
          key={key++}
          href={href}
          style={linkStyle}
          onClick={(e) => {
            e.preventDefault();
            go(href);
          }}
        >
          {label}
        </a>
      ) : (
        // Anything off-site opens in its own tab, so the conversation survives.
        <a
          key={key++}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
        >
          {label}
        </a>
      )
    );
    last = match.index + match[0].length;
  }

  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
};
